import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { useEffect, lazy } from 'react'
import { pageview } from './utils/analytics'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

function AnalyticsRouteTracker() {
  // Only fire on actual route (pathname) changes — NOT on every query-string
  // update.  SearchResultsPage and others call `navigate(..., {replace:true})`
  // repeatedly as filters / place coords / "search this area" resolve, and
  // counting each of those as a brand-new page_view was inflating pageview
  // counts and causing GA4 to report ~0s engagement on the search page
  // (engagement time = gap between consecutive events on a path; the last
  // page_view never had a follow-up event so it always logged as 0).
  const { pathname } = useLocation()

  useEffect(() => {
    // Delay long enough for each page's useEffect to set document.title
    // before we fire the page_view — fixes wrong/stale titles in GA4.
    // Only use gtag here; Firebase Analytics SDK is disabled below to
    // prevent double-counting the same measurement ID.
    const timer = window.setTimeout(() => {
      // Read search at fire time so the params recorded are the current ones,
      // not whatever they were when the route first mounted.
      const search = window.location.search
      pageview({
        path: `${pathname}${search}`,
        location: window.location.href,
        title: document.title,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [pathname])

  return null
}
import { AuthProvider } from './contexts/AuthContext'
import { AuthModalProvider } from './contexts/AuthModalContext'
import { AppleMapsProvider } from './contexts/AppleMapsContext'
import Layout from './components/Layout'

// Eager — homepage is the landing page (and is prerendered), so keep it in the
// main bundle for an immediate mount.
import HomePage from './pages/HomePage'

// SearchResultsPage is heavy (HotelMap, AppleMapPreview, CalendarPicker, ~50
// react-icons). It used to be eager "as a common entry point", but that dragged
// all of it into the main bundle and blocked first paint for homepage visitors
// who never search. Lazy-load it so it only downloads when /hotels is visited.
const SearchResultsPage = lazy(() => import('./pages/SearchResultsPage'))

// Lazy — each splits into its own chunk, loaded only when the route is visited.
const StaysPage = lazy(() => import('./pages/StaysPage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SignUpPage = lazy(() => import('./pages/SignUpPage'))
const SupportPage = lazy(() => import('./pages/SupportPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const CookiesPage = lazy(() => import('./pages/CookiesPage'))
const JobsPage = lazy(() => import('./pages/JobsPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const PartnershipsPage = lazy(() => import('./pages/PartnershipsPage'))
const CardPage = lazy(() => import('./pages/CardPage'))
const ExploreIndex = lazy(() => import('./pages/ExploreIndex'))
const ExplorePage = lazy(() => import('./pages/ExplorePage'))
const AuthActionPage = lazy(() => import('./pages/AuthActionPage'))
const RentalsSearchPage = lazy(() => import('./pages/RentalsSearchPage'))
const HotelDetailsPage = lazy(() => import('./pages/HotelDetailsPage'))
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'))
const CheckoutConfirmPage = lazy(() => import('./pages/CheckoutConfirmPage'))
const BookingLookupPage = lazy(() => import('./pages/BookingLookupPage'))
const FlightResultsPage = lazy(() => import('./pages/FlightResultsPage'))
const FlightCheckoutPage = lazy(() => import('./pages/FlightCheckoutPage'))
const FlightCheckoutConfirmPage = lazy(() => import('./pages/FlightCheckoutConfirmPage'))
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'))

function App() {
  return (
    <AuthProvider>
      <AuthModalProvider>
        <AppleMapsProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsRouteTracker />
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="stays" element={<StaysPage />} />
                <Route path="hotels" element={<SearchResultsPage />} />
                <Route path="rentals" element={<RentalsSearchPage />} />
                <Route path="flights" element={<FlightResultsPage />} />
                {/* Crawlable canonical route /flights/BTV-HNL. Static segments
                    (checkout) outrank this dynamic one in react-router ranking. */}
                <Route path="flights/:route" element={<FlightResultsPage />} />
                <Route path="flights/checkout" element={<FlightCheckoutPage />} />
                <Route path="flights/checkout/confirm" element={<FlightCheckoutConfirmPage />} />
                <Route path="hotel/:hotelId/:slug?" element={<HotelDetailsPage />} />
                <Route path="checkout" element={<CheckoutPage />} />
                <Route path="checkout/confirm" element={<CheckoutConfirmPage />} />
                <Route path="booking" element={<BookingLookupPage />} />
                <Route path="favorites" element={<FavoritesPage />} />
                <Route path="account" element={<AccountPage />} />
                <Route path="account/verification" element={<AuthActionPage />} />
                <Route path="login" element={<LoginPage />} />
                <Route path="signup" element={<SignUpPage />} />
                <Route path="support" element={<SupportPage />} />
                <Route path="terms" element={<TermsPage />} />
                <Route path="privacy" element={<PrivacyPage />} />
                <Route path="cookies" element={<CookiesPage />} />
                <Route path="jobs" element={<JobsPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="partnerships" element={<PartnershipsPage />} />
                <Route path="card" element={<CardPage />} />
                <Route path="rentals/:hotelId" element={<HotelDetailsPage />} />
                <Route path="explore" element={<ExploreIndex />} />
                <Route path="explore/:destination" element={<ExplorePage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </AppleMapsProvider>
      </AuthModalProvider>
    </AuthProvider>
  )
}

export default App
